import { ChevronDown, LayoutGrid, LogOut, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback } from '~/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { useAuth } from '~/helpers/store/auth'
import { useStatsNoticeDialog } from '~/helpers/store/consent'
import { cn } from '~/lib/utils'

type Variant = 'light' | 'dark'

/**
 * Menu déroulant apprenant partagé entre les headers « Mes sessions » et
 * « Lecteur SCORM » pour un rendu identique partout. Regroupe l'identité,
 * l'accès à la gestion des données personnelles (RGPD) et la déconnexion.
 */
function LearnerUserMenu({
  variant = 'light',
  showBackToSessions = false,
}: {
  variant?: Variant
  showBackToSessions?: boolean
}) {
  const { t } = useTranslation()
  const { payload } = useAuth()
  const navigate = useNavigate()
  const openStatsNotice = useStatsNoticeDialog(s => s.open)

  if (!payload) return null

  const initials = `${payload.firstName?.[0] ?? ''}${payload.lastName?.[0] ?? ''}` || 'U'
  const isDark = variant === 'dark'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex items-center gap-2 rounded-lg border px-3 py-1.5 transition-colors focus:outline-none cursor-pointer',
          isDark
            ? 'border-neutral-600 bg-neutral-800/60 text-white hover:border-neutral-400 hover:bg-neutral-700'
            : 'border-border bg-background hover:bg-accent',
        )}
      >
        <Avatar className="size-7 rounded-full">
          <AvatarFallback className={cn('rounded-full text-xs', isDark && 'bg-neutral-700 text-white')}>
            {initials}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium">
          {payload.firstName}
          {' '}
          {payload.lastName}
        </span>
        <ChevronDown className={cn('size-4', isDark ? 'text-neutral-400' : 'text-muted-foreground')} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52 rounded-lg">
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
            <Avatar className="size-8 rounded-full">
              <AvatarFallback className="rounded-full">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{`${payload.firstName} ${payload.lastName}`}</span>
              <span className="truncate text-xs text-muted-foreground">{payload.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {showBackToSessions && (
          <DropdownMenuItem onClick={() => navigate('/my-sessions')} className="cursor-pointer">
            <LayoutGrid className="mr-2 size-4" />
            {t('scorm.player.backToSessions')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={openStatsNotice} className="cursor-pointer">
          <ShieldCheck className="mr-2 size-4" />
          {t('notice.stats.manage')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/logout')} className="cursor-pointer">
          <LogOut className="mr-2 size-4" />
          {t('common.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default LearnerUserMenu
